import { FC } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import { ITeamHooperSeriesMetric } from '../../../../models/injury';
import { ChartEmptyState } from '../../AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import {
  chartAxisTick,
  chartGridProps,
  chartLegendStyle,
  chartTooltipContentStyle,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
} from '../../../../shared/charts/chartTheme';

interface Props {
  data: ITeamHooperSeriesMetric | null;
  height?: number;
}

// sleepQuality resta ciano: eccezione ammessa per distinguere una 5ª serie
// oltre ai token semantici (error/warning/text-0/text-2) già usati dalle altre.
const LINE_CONFIG = [
  { key: 'fatigue', stroke: 'var(--c-error)', label: 'Fatica' },
  { key: 'sleepQuality', stroke: '#5BC9E0', label: 'Qualita sonno' },
  { key: 'muscleSoreness', stroke: 'var(--c-warning)', label: 'Dolori muscolari' },
  { key: 'stressLevel', stroke: 'var(--c-text-0)', label: 'Stress' },
  { key: 'overall', stroke: 'var(--c-text-2)', label: 'Media team' },
] as const;

export const TeamHooperSeriesChart: FC<Props> = ({ data, height }) => {
  if (!data || !data.series || data.series.length === 0) {
    return <ChartEmptyState />;
  }

  const hasOverall = data.series.some((s) => s.overall != null);

  return (
    <ResponsiveContainer width="100%" height={height ?? 220}>
      <LineChart data={data.series} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
        <CartesianGrid {...chartGridProps} />
        <XAxis
          dataKey="date"
          tickFormatter={(v) => dayjs(v).format('DD/MM')}
          tick={chartAxisTick}
        />
        <YAxis domain={[1, 5]} tick={chartAxisTick} />
        <Tooltip
          contentStyle={chartTooltipContentStyle}
          labelStyle={chartTooltipLabelStyle}
          itemStyle={chartTooltipItemStyle}
          formatter={(value, name) => {
            const config = LINE_CONFIG.find((c) => c.key === name);
            return [value != null ? value : '–', config?.label ?? name];
          }}
          labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
        />
        <Legend
          formatter={(value) => {
            const config = LINE_CONFIG.find((c) => c.key === value);
            return config?.label ?? value;
          }}
          wrapperStyle={chartLegendStyle}
        />
        {LINE_CONFIG.filter((cfg) => cfg.key !== 'overall').map((cfg) => (
          <Line
            key={cfg.key}
            type="monotone"
            dataKey={cfg.key}
            stroke={cfg.stroke}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        ))}
        {hasOverall && (
          <Line
            type="monotone"
            dataKey="overall"
            stroke="var(--c-text-2)"
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
            connectNulls
          />
        )}
        {data.injuryMarkers?.map((marker) => (
          <ReferenceLine
            key={marker.injuryId}
            x={marker.date}
            stroke="var(--c-error)"
            strokeWidth={1.5}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
